import * as _ from 'underscore';

import WebHttpClient from '../httpClient/WebHttpClient';
import {incrementRouterSpaceReactKey} from '../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import {getAppStore} from '../app/AppStore';
import {
  CompositeSessionInfoSelectors,
  SessionServerSelectors,
  SessionSiteSelectors,
  SessionUserSelectors,
} from '../app/selectors/SessionInfoSelectors';
import {AppState} from '../app/state/AppState';
import {Messages} from '../codegen/Localize';
import {clearMetricsData} from '../metrics/actions/MetricsDataActions';
import {FeatureFlags} from '../ts/FeatureFlags';
import {CommonErrorCode, SiteSwitchErrorCodes} from '../ts/helpers/ErrorCodes';
import {Set} from '../ts/models/Set';
import {CreateApiTokenRequest} from '../ts/ServerApi/requests/CreateApiTokenRequest';
import {DeleteApiTokenRequest} from '../ts/ServerApi/requests/DeleteApiTokenRequest';
import {GeneratePublicKeyRequest, IGeneratePublicKeyResult} from '../ts/ServerApi/requests/GeneratePublicKeyRequest';
import {GetApiTokenRequest} from '../ts/ServerApi/requests/GetApiTokenRequest';
import {GetFeatureValueRequest} from '../ts/ServerApi/requests/GetFeatureValueRequest';
import {GetSessionInfoRequest, IGetSessionInfoResult} from '../ts/ServerApi/requests/GetSessionInfoRequest';
import {InitializeServerWithActiveDirectoryUserRequest} from '../ts/ServerApi/requests/InitializeServerWithActiveDirectoryUserRequest';
import {InitializeServerWithExternalUserRequest} from '../ts/ServerApi/requests/InitializeServerWithExternalUserRequest';
import {InitializeServerWithLocalUserRequest} from '../ts/ServerApi/requests/InitializeServerWithLocalUserRequest';
import {RecordNavigationTimingRequest} from '../ts/ServerApi/requests/RecordNavigationTimingRequest';
import {Request} from '../httpClient/serverApi/requests/Request';
import {SwitchSiteRequest} from '../ts/ServerApi/requests/SwitchSiteRequest';
import {IError} from '../httpClient/serverApi/types/IError';
import {IFeatureFlagsDoNotUse} from '../ts/ServerApi/types/IFeatureFlagsDoNotUse';
import {ILoginParams} from '../ts/ServerApi/types/ILoginParams';
import {IResult} from '../httpClient/serverApi/types/IResponse';
import {AuthenticationType, ICustomLogo} from '../settings/types/IServerSettingsUnauthenticated';
import {IServerVersionInfo, ISessionInfo, IUserSessionInfo, IUserTimeZone} from '../ts/ServerApi/types/ISessionInfo';
import {ISiteNameWithRole} from '../ts/ServerApi/types/ISiteNameWithRole';
import {SiteRoles} from '../ts/ServerApi/types/ISiteRole';
import {SessionService} from '../commonInfra/services/SessionService';

import {MobileAppService} from '../mobile/services/MobileAppService';
import {WindowLocationService} from '../commonInfra/services/WindowLocationService';
import {LocalizationLoader} from '../commonInfra/services/LocalizationLoader';
import {ServerSettingsUnauthenticated} from '../settings/services/ServerSettingsUnauthenticated';
import AxiosHttpClient from '../httpClient/AxiosHttpClient';
import {ServiceLocator} from '../app/ServiceLocator';
import {IQService} from 'angular';
import {IOAuthCredentialTypeInfo} from '../ts/ServerApi/types/IOAuthCredential';
import {FeatureFlagsHelper} from '../ts/FeatureFlagsHelper';
import {refreshSessionInfo} from '../app/actions/RefreshSessionInfoAction';
import {IToast, ToasterService, ToastType} from '../ts/services/ToasterService';
import * as moment from 'moment-timezone';
import {
  IUpdateUserLocalTimeZoneDataParams,
  UpdateUserLocalTimeZoneDataRequest,
} from '../ts/ServerApi/requests/UpdateUserLocalTimeZoneDataRequest';
import {InitializeUserNlpOnboardingStageRequest} from '../ts/ServerApi/requests/InitializeUserNlpOnboardingStageRequest';
import {L10n} from '../ts/L10n/L10n';
import { ExtractEncryptionMode } from '../sites/SitePropertyTypes';

// Start at 7-digit numbers so these don't collide with server error codes
export enum ErrorReason {
  Canceled = 1000001,
  ResourceNotFound = 1000002,
  Unauthorized = 1000003,
  Unknown = 1000004,
  EmptyResponse = 1000005,
}

export enum AuthLevel {
  Anonymous,
  Authenticated,
  SiteAdmin,
  SelfOrAdmin,
  ServerAdmin
}

export enum SiteDefaultAuth {
  Local,
  ActiveDirectory,
  SAML,
  External
}

class SessionManagementService {
  private caseInsensitiveSiteUrlNames: Set<string>;
  protected sessionInfoPromise: Promise<void>;
  protected numSessionInfoRequests: number;
  protected sessionInfo: ISessionInfo;
  private rebuildingSearchIndexToast: IToast;
  private publishingSamplesToast: IToast;
  private toaster = ToasterService.instance;
  private deferUntilSessionExists: ng.IDeferred<void>;

  constructor() {
    this.numSessionInfoRequests = 0;
    this.sessionInfoPromise = null;
    this.caseInsensitiveSiteUrlNames = new Set((siteUrlName: string) => siteUrlName.toLowerCase());
  }

  /**
   * Make a server request that needs to be made within a valid session.
   * If there is a request pending that might change the session, wait until that request is finished before sending this one.
   */
  public sendRequestWithinSession<TResult>(makeRequest: () => Promise<TResult>): Promise<TResult> {
    // Wait until any pending session info request is resolved before attempting the request
    if (this.numSessionInfoRequests > 0) {
      return Promise.resolve(this.sessionInfoPromise.then(makeRequest));
    }
    return makeRequest();
  }

  /**
   * Requests that could change the session must make the request through this method
   */
  public sessionInfoRequest(makeRequest: () => ng.IPromise<any>): ng.IPromise<any> {
    if (this.numSessionInfoRequests > 0) {
      // If existing session info request, make this request once it is completed.
      this.sessionInfoPromise = this.sessionInfoPromise.then(makeRequest);
    } else {
      // TODO: remove the static cast
      this.sessionInfoPromise = makeRequest() as Promise<any>;
    }
    this.numSessionInfoRequests++;
    this.sessionInfoPromise.finally(() => {
      this.numSessionInfoRequests--;
      if (this.deferUntilSessionExists && this.numSessionInfoRequests === 0 && this.inSession()) {
        this.deferUntilSessionExists.resolve();
      }
    }).catch(err => err);
    return this.sessionInfoPromise;
  }

  public initialize(username: string, password: string, displayName: string): ng.IPromise<void> {
    return this.sessionInfoRequest(() => {
      return WebHttpClient.requestPost(new GeneratePublicKeyRequest())
        .then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
          const encryptedPassword = rsa.encrypt(password, generatePublicKeyResult.key);
          let initializeRequest: Request<ILoginParams, ISessionInfo>;

          if (ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory()) {
            initializeRequest = new InitializeServerWithActiveDirectoryUserRequest({
              username: username,
              encryptedPassword: encryptedPassword,
              keyId: generatePublicKeyResult.keyId,
            });
          } else if (ServerSettingsUnauthenticated.isServerDomainFamilyLocal()) {
            initializeRequest = new InitializeServerWithLocalUserRequest({
              username: username,
              encryptedPassword: encryptedPassword,
              keyId: generatePublicKeyResult.keyId,
              displayName: displayName,
            });
          } else if (ServerSettingsUnauthenticated.isServerDomainFamilyExternal()) {
            initializeRequest = new InitializeServerWithExternalUserRequest({
              username: username,
              encryptedPassword: encryptedPassword,
              keyId: generatePublicKeyResult.keyId,
            });
          } else {
            throw new Error('Unknown domain family type. Current implementation only supports: AD, local, and external.');
          }
          return WebHttpClient.requestPost(initializeRequest)
            .then((initializeResult: ISessionInfo) => {
              if (initializeResult.errors) {
                return this.getQService().reject(initializeResult.errors[0] && initializeResult.errors[0].code);
              }
              return this.applySessionInfoInClient(initializeResult);
            });
        });
    });
  }


  /**
   * Returned promise will be fulfilled. All rejections are handled.
   * If there is an error, the code will be returned in the fulfilled handler.
   */
  public refreshSessionInfo(checkForEqualSite: boolean = false): ng.IPromise<any> {
    const previousUserId = this.sessionUserId();
    return this.sessionInfoRequest(() => {
      return WebHttpClient.requestPost(new GetSessionInfoRequest())
        .then((result: IGetSessionInfoResult) => {
          if (!result) {
            this.clearSessionInfo('error getting session info');
            return null;
          }
          if (result.errors) {
            this.clearSessionInfo(result.errors[0] && result.errors[0].code + '');
            return result.errors[0] && result.errors[0].code;
          } else {
            // in certain cases we only want to update session info if the returned session info matches the current site.
            // TFSID:953116. Switching quickly between two tabs of different sites on same server leads to bugs.
            // This site equality check prevents that
            if (checkForEqualSite && SessionSiteSelectors.siteId(getAppStore().getState()) !== result.site.id) {
              return;
            }
            return this.applySessionInfoInClient(result)
              .then((result) => {
                this.reloadStateIfUserChanged(previousUserId);
                return result;
              });
          }
        }, (errorResult: any) => {
          // TFSID 1067441: See AuthenticationService.request for more information. Don't clear the session when there is an
          // empty response. Empty responses mean that the request was aborted, and clearing the session in this case
          // breaks deep linking + makes for a bad UX.
          if (errorResult !== ErrorReason.EmptyResponse) {
            this.clearSessionInfo('error getting session info: ' + errorResult);
          }
        })
        .catch((reason: any) => {
          // See above comment!
          if (reason !== ErrorReason.EmptyResponse) {
            this.clearSessionInfo(reason);
          }
          return reason;
        });
    });
  }

  public hasCreatorTier(): boolean {
    return this.siteCapacityCheckEnabled() ? this.siteHasCreatorCapacity() : this.serverHasCreatorCapacity();
  }

  public hasViewerTier(): boolean {
    if (this.hasCreatorTier()) {
      return true;
    }
    return this.siteCapacityCheckEnabled() ? this.siteHasViewerCapacity() : this.serverHasViewerCapacity();
  }

  /**
   * Loads all state needed to render the app
   * Returns a promise that fulfills with a code indicating bootstrap state
   */
  public bootstrapSession(): ng.IPromise<number> {
    let sessionInfoNoSessionReason: number = null;
    return this.getQService().all([
      ServerSettingsUnauthenticated.fetchServerSettingsUnauthenticated(),
      this.refreshSessionInfo(),
    ]).then((results: any[]): ng.IPromise<any> => {
      // Try to deep link to the mobile app as soon as possible, to avoid the mobile browser's auth dialog popping up,
      // which prevents iOS Safari from showing the deep linking dialog "Open this page in Tableau".
      // Note: If we try to do this in run.ts as the first thing when we initialize, the redirects triggered by
      // the deep linking logic interfere with the completion of getSessionInfo calls resulting in the user getting
      // signed out.
      // For more details and vizclient counterpart, see https://reviewboard.prod.tableautools.com/r/253096
      // Also, we need to get server settings first so as not to deep link if the setting has been turned off on server.
      if (ServerSettingsUnauthenticated.isEnableDeepLinking()) {
        MobileAppService.tryDeepLinkOnLoad(WindowLocationService);
      }

      sessionInfoNoSessionReason = results[1];
      // Now that we have server settings and session info we can determine the language to display

      const language = this.sessionLanguage() ?? ServerSettingsUnauthenticated.serverLanguage();
      const locale = this.sessionLocale() ?? ServerSettingsUnauthenticated.serverLocale();

      return LocalizationLoader.updateLanguageAndLocaleInClient(language, locale);
    }).then((): ng.IPromise<number> => {
      return this.getQService().when(sessionInfoNoSessionReason);
    });
  }

  /**
   * This won't handle errors. Use SiteSwitchService.switchIfNeeded
   * to get the proper error feedback
   */
  public switchSiteIfNeeded_DoNotCallDirectly(urlName: string, podRedirectPath?: string): ng.IPromise<any> {
    return this.sessionInfoRequest(() => {
      // falsy values should be empty string for default site
      urlName = urlName || '';

      if (!this.isCurrentSiteUrlName(urlName)) {

        return WebHttpClient.requestPost(new SwitchSiteRequest({urlName: urlName}))
          .then((sessionInfo: ISessionInfo) => {
            if (sessionInfo && sessionInfo.errors) {
              const error = sessionInfo.errors[0];
              if (error && error.code === SiteSwitchErrorCodes.AttemptedToSwitchSiteToCurrentSite) {
                // No-op, do nothing
                // Remember that this particular site url name is case insensitive
                this.caseInsensitiveSiteUrlNames.add(urlName);
                this.refreshSessionInfo(); // TFSID: 503672 refresh in case stale data caused this
                return;
              }
              return this.getQService().reject(error);
            }
            this.applySessionInfoInClient(sessionInfo);
            getAppStore().dispatch(clearMetricsData());
          }, (error: IError) => {
            if (error && error.code === CommonErrorCode.RedirectToOtherPod) {
              this.redirectToOtherPod(error, podRedirectPath);
              // If redirected, defer indefinitely, to prevent further execution here
              return this.getQService().defer().promise;
            }
          });
      }
      return this.getQService().when(null);
    });
  }

  public recordNavigationTiming(timing: PerformanceTiming): void {
    //==== IE8 bug workaround: ====
    // Convert to plain ol' object with only the numbers from the browser's PerformanceTiming type.
    // Needed in order to send in request body in IE8.
    let timingObj = _.clone(timing);
    // For non IE8 browsers, remove functions (e.g. toJSON) so the browser doesn't attempt to serialize them.
    timingObj = _.omit(timingObj, _.functions(timingObj));

    WebHttpClient.requestPost(new RecordNavigationTimingRequest({timing: timingObj}));
  }

  public redirectToOtherPod(error: IError, destinationPodPath: string): void {
    const destinationPodUrl: string = AxiosHttpClient.getDestinationPodFromError(error);
    const dest = destinationPodUrl + destinationPodPath;
    WindowLocationService.navigateToWithoutAddingToHistory(dest);
  }

  public isCurrentSiteUrlName(urlName: string): boolean {
    if (this.caseInsensitiveSiteUrlNames.contains(urlName)) {
      return this.sessionSiteUrlName().toLowerCase() === urlName.toLowerCase();
    }

    return this.sessionSiteUrlName() === urlName;
  }

  // username and domainName are only needed for checking SELF
  public isUserAuthorized(role: AuthLevel, userName: string = '', domainName: string = ''): boolean {
    switch (role) {
      case AuthLevel.Anonymous:
      default:
        return true;
      case AuthLevel.Authenticated:
        return this.inSession();
      case AuthLevel.SiteAdmin:
        return this.isSessionSiteOrServerAdmin();
      case AuthLevel.SelfOrAdmin:
        // for the user place page, return true if an admin or the URL contains the
        // username and domainName of the session user
        return this.isSessionSiteOrServerAdmin() || this.isSessionUserSelf(userName, domainName);
      case AuthLevel.ServerAdmin:
        return this.isSessionServerAdmin();
    }
  }

  /**
   * This is a convenience method to get the app state for the various session info selectors below
   * as we port their sources of truth to `SessionInfoSelectors.tsx` over time.
   */
  private appState(): AppState {
    return getAppStore().getState();
  }

  /**
   * Returns the entire session info object. If using this, be careful not to cache it; it's better
   * to use the getter methods below to fetch individual pieces each time so we know it's not stale.
   */
  public sessionInfo_useWithCare(): ISessionInfo {
    return this.sessionInfo;
  }

  public sessionUserInfo(): IUserSessionInfo {
    return this.sessionInfo && this.sessionInfo.user;
  }

  public isViewerUser(): boolean {
    return SessionSiteSelectors.siteRole(this.appState()) === SiteRoles.Viewer;
  }

  public sessionUserDisplayName(): string {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.displayName || '';
  }

  public sessionUserId(): string {
    return SessionUserSelectors.sessionUserId(this.appState());
  }

  public sessionAuthUserId(): string {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.authId;
  }

  public sessionSiteLuid(): string {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.luid;
  }

  public sessionUsername(): string {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.username;
  }

  public sessionUserDomainName(): string {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.domainName;
  }

  public isDataAlertsEnabled(): boolean {
    return SessionSiteSelectors.isDataAlertsEnabled(this.appState());
  }

  public catalogingOnSiteEnabled(): boolean {
    return FeatureFlags.metadataAreaEnabled &&
      this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.catalogingOnSiteEnabled !== false;
  }

  public catalogingOnServerEnabled(): boolean {
    return FeatureFlags.metadataAreaEnabled &&
      this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.catalogingOnServerEnabled !== false;
  }

  public flowsOnSiteEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.flowsOnSiteEnabled !== false;
  }

  public flowsOnServerEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.flowsOnServerEnabled !== false;
  }

  public flowProcessorEnabled(): boolean {
    return this.flowsOnServerEnabled() || FeatureFlags.MaestroFlowEditingServerV1;
  }

  public editingFlowsEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.editingFlowsEnabled !== false;
  }

  public schedulingFlowsEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.schedulingFlowsEnabled !== false;
  }

  public get isAfeUiSettingEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.useAlternativeFederationEngine === true;
  }

  public canSessionUserEditEmbeddedCredentials(): boolean {
    return this.isSessionSiteOrServerAdmin() || (this.sessionInfo && this.sessionInfo.server.embeddedCredentialsEnabled);
  }

  public isSessionUserSelf(userName: string, domainName: string): boolean {
    return this.sessionInfo && this.sessionInfo.user
      && this.sessionInfo.user.username === userName
      && this.sessionInfo.user.domainName === domainName;
  }

  public isSessionServerAdmin(): boolean {
    return SessionUserSelectors.isSessionServerAdmin(this.appState());
  }

  public isSessionSiteAdmin(): boolean {
    return SessionSiteSelectors.isSessionSiteAdmin(this.appState());
  }

  public isSessionSiteOrServerAdmin(): boolean {
    return CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(this.appState());
  }

  public sessionSiteId(): string {
    return SessionSiteSelectors.siteId(this.appState());
  }

  public sessionSiteRole(): string {
    return SessionSiteSelectors.siteRole(this.appState());
  }

  public sessionServerId(): string {
    return this.sessionInfo ? this.sessionInfo.server.logicalServerInstanceId : '';
  }

  public sessionSite(): ISiteNameWithRole {
    return this.sessionInfo && this.sessionInfo.site;
  }

  public sessionSiteUrlName(): string {
    return SessionSiteSelectors.siteUrlName(this.appState());
  }

  public sessionSiteName(): string {
    return SessionSiteSelectors.siteName(this.appState());
  }

  public sessionServerVersion(): IServerVersionInfo {
    return this.sessionInfo ? this.sessionInfo.server.version : null;
  }

  public sessionServerPodId(): string {
    return this.sessionInfo ? this.sessionInfo.server.podId : null;
  }

  public sessionSupportHost(): string {
    return this.sessionInfo && this.sessionInfo.server.supportHost;
  }

  public sessionSupportPath(): string {
    return this.sessionInfo && this.sessionInfo.server.supportPath;
  }

  public webstoreUrl(): string {
    return this.sessionInfo && this.sessionInfo.server ? this.sessionInfo.server.webstoreUrl : '';
  }

  public isSessionMultiSite(): boolean {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.numberOfSites > 1;
  }

  public isBuyLicensesEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.site.buyLicensesEnabled;
  }

  public isVersioningEnabled(): boolean {
    return SessionSiteSelectors.isVersioningEnabled(this.appState());
  }

  public siteLogo(): ICustomLogo {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.customHeaderLogo;
  }

  public compactLogo(): ICustomLogo {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.customCompactLogo;
  }

  public isSiteCustomizationEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.siteCustomizationEnabled;
  }

  public isTabAdminServiceEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.tabAdminServiceEnabled;
  }

  public isRecommendationsEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.recommendationsEnabled;
  }

  public isVizRecommendationsEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.vizRecommendationsEnabled;
  }

  public isSiteAdminSettingsEnabled(): boolean {
    return SessionServerSelectors.siteAdminSettingsEnabled(this.appState());
  }

  public siteUuid(): string {
    return this.sessionInfo && this.sessionInfo.site ? this.sessionInfo.site.siteUuid : null;
  }

  public siteTypeName(): string {
    return this.sessionInfo && this.sessionInfo.site ? this.sessionInfo.site.siteTypeName : null;
  }

  /**
   * Returns a promise that resolves when all pending session info requests are completed.
   * This can be used in places where we don't want to get properties from the session when we know
   * they might be changing.
   * NOTE: This promise does not guarantee that there is a session when resolved; it only guarantees
   * that there are no in-progress pending API requests to fetch the session info. If you need to wait
   * for an actual session (e.g. at the login screen), use waitForSession() instead.
   */
  public getSessionInfoPromise(): ng.IPromise<void> {
    return this.sessionInfoPromise ? this.sessionInfoPromise : this.getQService().when();
  }

  /**
   * Returns a promise that resolves when all pending session info requests are completed, AND
   * when there is a valid session. i.e. the promise will not resolve until after the user logs in.
   * This is different than getSessionInfoPromise(), which only looks at pending session info
   * requests and may resolve without a valid session.
   */
  public waitForSession(): ng.IPromise<void> {
    if (this.inSession()) {
      return this.getSessionInfoPromise();
    }

    if (!this.deferUntilSessionExists) {
      this.deferUntilSessionExists = this.getQService().defer();
    }
    return this.deferUntilSessionExists.promise;
  }

  public updateDisplayNameAllowed(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.updateDisplayNameAllowed;
  }

  public defaultSubscriptionsEmail(): string {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.defaultSubscriptionsEmail;
  }

  public featureFlagsDoNotUse(): IFeatureFlagsDoNotUse {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.featureFlags;
  }

  public changePasswordUrl(): string {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.changePasswordUrl;
  }

  public manageMfaUrl(): string {
    return this.isMfaEnabled() && this.sessionInfo.server && this.sessionInfo.server.manageMfaUrl;
  }

  public datasourceHelpUrl(): string {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.datasourceHelpUrl;
  }

  public offlineHelpEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.offlineHelpEnabled;
  }

  public helpEdition(): string {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.helpEdition;
  }

  public defaultDomain(): string {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.defaultDomain;
  }

  public isBetaPod(): boolean {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.server.betaPod;
  }

  public canSessionUserAuthorOnWeb(): boolean {
    return SessionUserSelectors.canAuthorOnWeb(this.appState());
  }

  public canSessionUserManageUsers(): boolean {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.canManageUsers;
  }

  public getStartPage(): string {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.startPage || '/';
  }

  public userTimeZone(): IUserTimeZone {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.userTimeZone;
  }

  public defaultAskDataMode(): string {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.defaultNewSiteAskDataMode || '';
  }

  public isAdminViewsDisabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.adminViewsDisabled;
  }

  public isAdminDataSourceEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.adminInsightsEnabled;
  }

  private siteHasCreatorCapacity(): boolean {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.serverSiteCapacityInfo && this.sessionInfo.site.serverSiteCapacityInfo.hasCreatorCapacity;
  }

  private siteHasViewerCapacity(): boolean {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.serverSiteCapacityInfo && this.sessionInfo.site.serverSiteCapacityInfo.hasViewerCapacity;
  }

  // Server Settings

  private serverHasCreatorCapacity(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.serverSiteCapacityInfo && this.sessionInfo.server.serverSiteCapacityInfo.hasCreatorCapacity;
  }

  private serverHasViewerCapacity(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.serverSiteCapacityInfo && this.sessionInfo.server.serverSiteCapacityInfo.hasViewerCapacity;
  }

  public siteCapacityCheckEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.siteCapacityCheckEnabled;
  }

  public shouldShowServerLicensingTierInfo(): boolean {
    return this.isSessionServerAdmin();
  }

  public hasSubscriptionTieredLicensing(): boolean {
    return this.hasCreatorTier() || this.hasViewerTier();
  }

  public displaySchedulesInClientTimezone(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.displaySchedulesInClientTimezone;
  }

  public displayScheduleDescriptionAsName(): boolean {
    return this.sessionInfo.server.displayScheduleDescriptionAsName;
  }

  public isRunNowEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.runNowOnServerEnabled;
  }

  public isUISiteCreationEnabled(): boolean {
    // the siteAdminSettings are only enabled in Tableau Online, using them as a signal for being in Online
    return !(this.isSiteAdminSettingsEnabled());
  }

  public isOnlineEnvironment(): boolean {
    return this.getHelpEdition() === 'online';
  }

  public isNotificationEnabledForServer(): boolean {
    return this.sessionInfo.server.notificationEnabledForServer;
  }

  public isNotificationEnabledForSite(): boolean {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.notificationEnabled;
  }

  public isSiteInviteEmailEnabledForServer(): boolean {
    return this.sessionInfo && this.sessionInfo.server.siteInviteBasicEmailEnabled;
  }

  public isWelcomeChannelEnabledForUser(): boolean {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.showWelcomeScreen;
  }

  public isMetricsBannerEnabledForUser(): boolean {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.showMetricsBanner;
  }

  public isAddSupportUserEnabledForServer(): boolean {
    return this.sessionInfo.server.featureFlags.addSupportUserEnabled;
  }

  public getBuyLicensesDisplayType(): string {
    return this.sessionInfo && this.sessionInfo.site.buyLicenseButtonDisplayType;
  }

  public isMfaEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.mfaEnabled;
  }

  public getFeatureValue(serviceName: string, defaultValue: boolean): Promise<any> {
    return WebHttpClient.sendRequest(new GetFeatureValueRequest(serviceName, String(defaultValue))).then((result) => {
      if (result.errors) {
        return Promise.reject(result.errors[0]);
      } else {
        return Promise.resolve(result);
      }
    });
  }

  public getApiTokens(): Promise<any> {
    return WebHttpClient.sendRequest(new GetApiTokenRequest()).then((result) => {
      if (result.errors) {
        return Promise.reject(result.errors[0]);
      } else {
        return Promise.resolve(result);
      }
    });
  }

  public createApiToken(clientName: string): Promise<any> {
    return WebHttpClient.sendRequest(new CreateApiTokenRequest(clientName)).then((result) => {
      if (result.errors) {
        return Promise.reject(result.errors[0]);
      } else {
        return Promise.resolve(result);
      }
    });
  }

  public deleteApiToken(tokenId: string): Promise<any> {
    return WebHttpClient.sendRequest(new DeleteApiTokenRequest(tokenId)).then((result) => {
      if (result.errors) {
        return Promise.reject(result.errors[0]);
      } else {
        return Promise.resolve(result);
      }
    });
  }

  public hideAdminVizPerformanceofFlowRuns(): boolean {
    return !(this.hideAdminVizPerformanceofFlowRunsOnSite() && this.flowProcessorEnabled());
  }

  public hideAdminVizPerformanceofFlowRunsOnSite(): boolean {
    return !(this.flowProcessorEnabled() && this.flowsOnSiteEnabled());
  }

  public isSelfServiceScheduleEnabled(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.featureFlags &&
      this.sessionInfo.server.featureFlags.selfServiceSchedulesEnabled;
  }

  public isExtractEncryptionModeDisabled(): boolean {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.extractEncryptionMode && this.sessionInfo.site.extractEncryptionMode === ExtractEncryptionMode.DISABLED;
  }

  public isLimitedUserVisibilityEnforced(): boolean {
    return CompositeSessionInfoSelectors.isLimitedUserVisibilityEnforced(this.appState());
  }

  public getExtractEncryptionModeForSite(): ExtractEncryptionMode {
    return this.sessionInfo && this.sessionInfo.site && this.sessionInfo.site.extractEncryptionMode;
  }

  public isExtractEncryptionEnabledOmniSwitch(): boolean {
    // Backend checks for 1) Feature Flags and 2) environment specific checkes. (e.g. online vs onprem) for populating sessionInfo.server.extractEncryptionEnabled
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.extractEncryptionEnabled === true;
  }

  public isNamedSharingEnabled() {
    // This will check and return site setting as well as this feature is controlled by FF and site setting and if current user is guest.
    return SessionSiteSelectors.isNamedSharingEnabled(this.appState());
  }

  public isRequestAccessEnabled() {
    return SessionSiteSelectors.isRequestAccessEnabled(this.appState());
  }

  public analyticsEndpoint(): string {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.analyticsEndpoint;
  }

  private reloadStateIfUserChanged(previousUserId: string) {
    const newUserId = this.sessionUserId();
    if (previousUserId && newUserId && newUserId !== previousUserId) {
      getAppStore().dispatch(incrementRouterSpaceReactKey());
    }
  }

  public ignoreExtractTaskPriority(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.ignoreExtractTaskPriority;
  }

  public maxDescriptionLength(): number {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.maxDescriptionLength;
  }

  private getHelpEdition(): string {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.helpEdition;
  }

  public getSessionIdleLimitSeconds() {
    // TODO: remove the 5 minute default when the backend implements this. B144748
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.sessionIdleLimitSeconds || 5 * 60;
  }

  public uploadSAMLMetadata(file: File): ng.IPromise<IResult> {
    const formData = new FormData();
    formData.append('entity_descriptor', file);
    formData.append(SessionService.getSessionCookieName(), SessionService.getSessionId());
    return WebHttpClient.requestPostFormToRestApi(formData, 'updateIdentityProvider').then((result: IResult) => {
      if (result.errors) {
        return this.getQService().reject(result.errors[0] && result.errors[0].code);
      } else {
        return result;
      }
    });
  }

  public applySessionInfoInClient(newSessionInfo: ISessionInfo): ng.IPromise<any> {
    this.sessionInfo = newSessionInfo;
    SessionService.createSession();
    if (newSessionInfo && newSessionInfo.server) {
      FeatureFlagsHelper.overwriteFeatureFlagsFromServerFeatureFlags(newSessionInfo.server.featureFlags, FeatureFlags);
    }
    this.updateUserTimeZone();
    this.initializeUserNlpOnboardingStage();
    getAppStore().dispatch(refreshSessionInfo(newSessionInfo));

    const newLanguage = this.sessionLanguage() ?? ServerSettingsUnauthenticated.serverLanguage();
    const newLocale = this.sessionLocale() ?? ServerSettingsUnauthenticated.serverLocale();

    return LocalizationLoader.updateLanguageAndLocaleInClient(newLanguage, newLocale).then(() => {
      this.showOrHideRebuildingSearchIndexToast();
      this.showOrHidePublishingSamplesToast();
    });
  }

  public isSandboxEnabledOnServer(): boolean {
    return FeatureFlags.personalSpaceEnabled && this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.personalSandboxEnabledOnServer;
  }

  public isSandboxEnabledByDefaultForNewSites(): boolean {
    return FeatureFlags.personalSpaceEnabled && this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.personalSandboxByDefaultEnabled;
  }

  public oauthSettings(): Array<IOAuthCredentialTypeInfo> {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.oauthSettings;
  }

  public isSessionRestrictedTrustedTicket(): boolean {
    return this.sessionInfo && this.sessionInfo.isRestrictedTrustedTicketSession;
  }

  public sessionLanguage(): string {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.language;
  }

  public sessionLocale(): string {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.locale;
  }

  public rebuildingSearchIndex(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.rebuildingSearchIndex;
  }

  public publishingSamples(): boolean {
    return this.sessionInfo && this.sessionInfo.server && this.sessionInfo.server.publishingSamples;
  }

  public isUserSetTimeZone(): boolean {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.isUserSetTimeZone;
  }

  public nlpOnboardingStage(): string | undefined {
    return this.sessionInfo && this.sessionInfo.user && this.sessionInfo.user.userNlpOnboardingStage;
  }

  /**
   * Uses moment.js to guess client time zone and makes a request to update.
   */
  public updateUserTimeZone() {

    if (this.isUserSetTimeZone() != null) {
      // only guess the timezone once and do not override user set time zone
      return;
    }
    const date = new Date();

    const timeZoneId = moment.tz.guess();
    const updateUserLocalTimeZoneDataParams: IUpdateUserLocalTimeZoneDataParams = {
      offset: date.getTimezoneOffset(),
      timeZoneId: timeZoneId,
      isUserSetTimeZone: false, // this is a system guess not a user action
    };
    const updateUserLocalTimeZoneDataRequest =
      new UpdateUserLocalTimeZoneDataRequest(updateUserLocalTimeZoneDataParams);
    WebHttpClient.requestPost(updateUserLocalTimeZoneDataRequest);
  }

  public initializeUserNlpOnboardingStage() {
    if (this.shouldInitializeUserNlpOnboardingStage()) {
      WebHttpClient.requestPost(new InitializeUserNlpOnboardingStageRequest({nlpOnboardingStage: 'None'}));
    }
  }

  public inSession(): boolean {
    return !!SessionService.isSessionCurrentlyValid();
  }

  public shouldInitializeUserNlpOnboardingStage(): boolean {
    return !this.isSessionRestrictedTrustedTicket() && this.nlpOnboardingStage() === undefined;
  }


  private showOrHideRebuildingSearchIndexToast() {
    if (this.inSession() && L10n.language && this.rebuildingSearchIndex()) {
      if (!this.rebuildingSearchIndexToast || this.rebuildingSearchIndexToast.isClosed) {
        this.toaster.pop({
          type: ToastType.Error,
          text: Messages.rebuildingSearchIndexMessage(),
        });
        this.rebuildingSearchIndexToast = this.toaster.toasts[this.toaster.toasts.length - 1];
      }
    } else {
      if (this.rebuildingSearchIndexToast) {
        this.toaster.close(this.rebuildingSearchIndexToast.id);
      }
    }
  }

  private showOrHidePublishingSamplesToast() {
    if (this.inSession() && L10n.language && this.publishingSamples()) {
      if (!this.publishingSamplesToast || this.publishingSamplesToast.isClosed) {
        this.toaster.pop({
          type: ToastType.Info,
          text: Messages.publishingSamplesMessage(),
          timeoutDelay: 60 * 1000, // 60 seconds
        });
        this.publishingSamplesToast = this.toaster.toasts[this.toaster.toasts.length - 1];
      }
    } else {
      if (this.publishingSamplesToast) {
        this.toaster.close(this.publishingSamplesToast.id);
      }
    }
  }

  public clearSessionInfo(message?: string) {

    // If someone is waiting for a session, but we didn't have a session, let them keep waiting.
    // Clear out the defer only if we were already in a session, at which point any clients who were
    // waiting for a session would have already had their promise resolved.
    if (this.inSession() && this.deferUntilSessionExists) {
      this.deferUntilSessionExists.reject('Session is being cleared');
      this.deferUntilSessionExists = null;
    }
    SessionService.endSession(message || '');
    this.sessionInfo = null;
  }

  private getQService(): IQService {
    return ServiceLocator.get<ng.IQService>('$q');
  }
}

const sessionManagementServiceInstance = new SessionManagementService();

export type {SessionManagementService};

export function getSessionManagementServiceInstance(): SessionManagementService {
  return sessionManagementServiceInstance;
}

export function getSessionManagementServiceNewInstanceForTestOnly(): SessionManagementService {
  return new SessionManagementService();
}
